import { useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/th"; // Thai short month names for the PDF date labels

import { imgList } from "@constants/imgList";
import {
  MetricDetailLayout,
  MetricFilterControls,
  SummaryBlock,
  useDeviceReport,
  timeColumn,
  numericColumn,
} from "@components/MetricDetail";
import type { FetchReport } from "@components/MetricDetail";
import Excel from "@components/Excel";
import Table from "@components/Table";
import ChartTemperatureHistory, {
  type ChartSeries,
} from "@components/Charts/ChartTemperatureHistory";
import { ModalFilterTemp, ModalExportReport } from "@components/Modals";
import { utcQueryStr, localAnchorStr } from "@utils/date";
import { apiGetReportWindow } from "@apis/reportWindow";
import { displayImage } from "@utils/image";
import { toFixedOrDash } from "@utils/string";
import { runReportAggregation } from "@utils/reportTableWorkerClient";
import {
  buildAlarmWindows,
  classifyBreachByWindows,
  heuristicAlarmWindows,
  rowAlarmSeverity,
  computeStats,
  detectBreachesDynamic,
  downsampleAvg,
  isBreachDynamic,
  recommendedResolution,
  type AlarmLog,
  type BreachSeverity,
} from "@utils/temperatureHistory";
import { Tooltip } from "antd";
import { filterTempAction } from "@reduxs/Actions/monitoringAction";
import { apiGet } from "@apis/gateway";
import { stateInterface } from "@reduxs/interface";

// Sensor fields averaged per data-resolution window (see downsampleAvg).
const TEMP_NUMERIC_KEYS = [
  "insideTemp",
  "realTemp",
  "evapCoil",
  "probe2R",
  "highestTemp",
  "lowestTemp",
  "setPointTemp",
];

// Plain download glyph for the export buttons — the ico_download asset has a
// baked-in yellow circle border, too busy at button size.
const DownloadIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// Tabler "adjustments" (filter) glyph — replaces the ico_filter raster so it
// stays crisp and takes the brand colour.
const AdjustmentsIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#919eab"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 10a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M6 4v4" />
    <path d="M6 12v8" />
    <path d="M10 16a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M12 4v10" />
    <path d="M12 18v2" />
    <path d="M16 7a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
    <path d="M18 4v1" />
    <path d="M18 9v11" />
  </svg>
);

// Tabler section-heading glyphs (brand accent).
const ChartLineIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FEBC11"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 19l16 0" />
    <path d="M4 15l4 -6l4 2l4 -5l4 4" />
  </svg>
);
const TableIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FEBC11"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 5a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-14z" />
    <path d="M3 10h18" />
    <path d="M10 3v18" />
  </svg>
);

export default function TemperatureDetail() {
  const filterTemp = useSelector(
    (state: stateInterface) => state.monitoring.filterTemp
  );
  // Logged-in user — stamped on the PDF as "issued by" (ref-certificate style)
  const userInfo = useSelector((state: stateInterface) => state.user.info);
  const [dataTemp, setDataTemp] = useState<any>({});
  const [modal, setModal] = useState({
    open: "",
  });
  // Sensor lines the user has toggled off on the chart (slice 3). The captured
  // PDF chart reflects this too, since it snapshots the live chart element.
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  // True only while snapshotting for the PDF — re-renders the chart without
  // the zoom brush so the exported chart is clean like the ref certificate.
  const [chartExportMode, setChartExportMode] = useState(false);
  // While exporting, the chart draws ONLY the series picked in the export
  // modal (null = follow the on-page chip toggles).
  const [exportSeriesKeys, setExportSeriesKeys] = useState<string[] | null>(
    null
  );
  const toggleSeries = (key: string) =>
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // Ground-truth alarm markers (device activity log, tHi/tLo = 1) for the
  // report window. null = not loaded / fetch failed → classification falls
  // back to the timeAlertDelay duration heuristic.
  const [alarmLogs, setAlarmLogs] = useState<AlarmLog[] | null>(null);
  const getReportTemp: FetchReport = async (filterDeviceDetail, language, id) => {
    const res = await apiGetReportWindow(
      (anchor: string) =>
        `${
          process.env.REACT_APP_API_DOMAIN
        }/monitoring-api/device-report/${id}/temperature?summarize=false&dateTime=${anchor}&dateTimeUnit=${filterDeviceDetail.unit}&tempUnit=${filterTemp.unit}`,
      localAnchorStr(filterDeviceDetail.date, filterDeviceDetail.period),
      filterDeviceDetail.unit,
      language
    );
    if (res.status && res.code === 200) {
      const dateTimeStr = `${dayjs(filterDeviceDetail.date).format(
        "YYYY-MM-DD"
      )} ${
        filterDeviceDetail.period.toString().length < 2
          ? `0${filterDeviceDetail.period}:00:00`
          : `${filterDeviceDetail.period}:00:00`
      }`;
      const results = await runReportAggregation("temp", {
        rawResult: res?.data?.data?.result ?? [],
        dateTimeStr,
        unit: filterDeviceDetail.unit,
      });
      setDataTemp({ results });

      // Ground-truth alarm markers for the same window (padded 1h each side
      // for boundary events + clock skew). The device posts tHi/tLo the moment
      // its alarm fires — its alert delay, including setting changes inside
      // the range, is already applied. Failure → null → duration heuristic.
      // ⚠ the endpoint interprets startDate/endDate as UTC while the report
      // range is local wall-clock — send UTC strings (utcQueryStr), or the
      // fetched window is shifted by the timezone offset (+7h here).
      const logRes = await apiGet(
        `${
          process.env.REACT_APP_API_DOMAIN
        }/monitoring-api/device-report/${id}/pagination/activity-logs?startDate=${utcQueryStr(
          dayjs(dateTimeStr).subtract(1, "hour")
        )}&endDate=${utcQueryStr(
          dayjs(dateTimeStr)
            .add(1, filterDeviceDetail.unit as any)
            .add(1, "hour")
        )}&limit=9999&page=1`,
        {},
        language
      );
      if (logRes.status && logRes.code === 200) {
        const rows: any[] = logRes?.data?.data?.data ?? [];
        // Keep BOTH markers: value 1 = alarm fired, value 0 = alarm cleared —
        // together they form the alarm-ACTIVE windows used for row severity.
        // The flag may arrive as number/string/boolean; coerce before use.
        const isOn = (v: any) => v === 1 || v === "1" || v === true;
        const logs = rows
          .filter(
            (l) => l?.data?.tHi !== undefined || l?.data?.tLo !== undefined
          )
          .map((l) => ({
            time: l?.dateTime ?? l?.createdAt ?? "",
            key: (l?.data?.tHi !== undefined ? "tHi" : "tLo") as "tHi" | "tLo",
            on: isOn(l?.data?.tHi !== undefined ? l?.data?.tHi : l?.data?.tLo),
          }));
        setAlarmLogs(logs);
      } else {
        setAlarmLogs(null);
      }
    }
  };
  const {
    t,
    currentLanguage,
    navigate,
    id,
    dispatch,
    deviceDetail,
    filterDeviceDetail,
    plan,
    init,
    listUnit,
    listPeriod,
    dataReportDetail,
    renderSound,
    getReport,
  } = useDeviceReport(getReportTemp);

  // Only the temperature unit (°C/°F) is sent to the API; view/value/resolution
  // are client-side, so a resolution change re-derives displayRows without a
  // refetch.
  useEffect(() => {
    if (init) getReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTemp.unit]);

  // When the period unit changes, snap the data-resolution to the recommended
  // default for that range (hours/days→1m, weeks→5m, months→30m) for perf +
  // readability. A manual pick in the filter modal persists until the next unit
  // change. Read filterTemp via ref so this fires ONLY on unit change, never
  // clobbering a manual resolution pick.
  const filterTempRef = useRef(filterTemp);
  filterTempRef.current = filterTemp;
  useEffect(() => {
    dispatch(
      filterTempAction({
        ...filterTempRef.current,
        resolution: recommendedResolution(filterDeviceDetail.unit),
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDeviceDetail.unit]);
  // The device's alarm delay (seconds): an excursion that returns in-range
  // before this elapses never trips the device alarm, so the report renders it
  // as a soft "brief" event (amber) instead of a full alarm (red). 0/absent =
  // immediate alarm on every excursion.
  const alertDelaySec =
    Number(
      deviceDetail?.controlInfo?.timeAlertDelay ??
        deviceDetail?.timeAlertDelay ??
        0
    ) || 0;

  // Out of range = the realtime inside probe (realTemp, the reading the device
  // alarm uses) breaches its per-record threshold (lowestTemp..highestTemp).
  // The row carries raw _real/_low/_high (below).
  // Shared white-card tooltip chrome (smartfinn pattern) — used by both the
  // breaching-cell tooltips and the Excel-button hint so they read as one
  // family. antd's default dark tooltip is hard to read with Thai text.
  const cardTooltipStyle = {
    color: "#0f172a",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.14)",
    padding: "10px 14px",
    fontSize: 13,
    lineHeight: 1.6,
    maxWidth: 320,
  } as const;

  const isTempBreach = (r: any) =>
    r?._real !== null &&
    r?._real !== undefined &&
    isBreachDynamic({
      value: r._real,
      min: r?._low ?? null,
      max: r?._high ?? null,
    });
  // insideTemp checked independently against the same per-record threshold, so
  // it can flag red on its own (mirrors the PDF: inside + realtime each colored
  // by their own value; the row highlight still tracks realTemp = the alarm).
  const isRoomBreach = (r: any) =>
    r?._room !== null &&
    r?._room !== undefined &&
    isBreachDynamic({
      value: r._room,
      min: r?._low ?? null,
      max: r?._high ?? null,
    });

  // For a row already known to breach: red only if the row's minute falls
  // inside an alarm-ACTIVE window (device fired → cleared, direction-matched);
  // amber otherwise — the within-delay lead-in of an excursion, or after the
  // alarm cleared. Rows carry raw _time/_real/_low/_high; windows live in
  // `summary` (initialized by the time render callbacks run). Averaged rows
  // are stamped with their window-START time, so the check spans the current
  // resolution window.
  const severityForRow = (r: any): "alarm" | "brief" => {
    const v = r?._real;
    const dir =
      v !== null && v !== undefined && r?._high !== null && v > r._high
        ? "high"
        : v !== null && v !== undefined && r?._low !== null && v < r._low
        ? "low"
        : "any";
    return rowAlarmSeverity(
      new Date(r?._time ?? "").getTime(),
      Math.max(1, filterTemp?.resolution ?? 1) * 60_000,
      dir,
      summary?.alarmWindows ?? []
    );
  };
  const breachTextStyle = (r: any) => ({
    fontWeight: 700,
    color: severityForRow(r) === "brief" ? "#d97706" : "#dc2626",
  });
  // Breaching cell wrapped in a hover tooltip explaining WHAT happened.
  // Light card-style tooltip (white bg, structured rows: severity pill →
  // explanation → threshold) ported from smartfinnbackoffice's role&permission
  // table — the default dark tooltip was hard to read with Thai text.
  const breachCell = (v: any, r: any) => {
    const brief = severityForRow(r) === "brief";
    return (
      <Tooltip
        color="#ffffff"
        overlayInnerStyle={cardTooltipStyle}
        title={
          <div>
            {/* Row 1: severity pill, color-matched to the cell */}
            <div style={{ marginBottom: 4 }}>
              <span
                style={{
                  background: brief ? "#fef3c7" : "#fee2e2",
                  color: brief ? "#b45309" : "#b91c1c",
                  borderRadius: 999,
                  padding: "1px 10px",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {brief ? t("detail.briefexcursion") : t("detail.outofrange")}
              </span>
            </div>
            {/* Row 2: what happened */}
            <div style={{ color: "#475569", marginBottom: 4 }}>
              {brief ? t("detail.tipbrief") : t("detail.tipalarm")}
            </div>
            {/* Row 3: the threshold in effect on THIS row */}
            <div style={{ fontSize: 12, color: "#94a3b8" }}>
              {t("detail.thresholdrange")}: {r?._low ?? "-"} — {r?._high ?? "-"}
              {tempUnitSuffix}
            </div>
          </div>
        }
      >
        <span style={{ ...breachTextStyle(r), cursor: "help" }}>{v}</span>
      </Tooltip>
    );
  };

  // Short table-only headers (the full labels wrap to a hard-to-read second line
  // at this column width). Excel/PDF/chart-legend keep the full-length labels.
  // Each column is tagged with its chart-series key so the on-page legend chips
  // (hiddenSeries) drive column visibility — the chart is the single control for
  // both the lines and the table columns. The time column has no key → always on.
  const columnDefs: { seriesKey?: string; col: any }[] = [
    { col: timeColumn(t) },
    {
      seriesKey: "setPointTemp",
      col: numericColumn(t, "detail.tblsettemp", "set_temp"),
    },
    {
      seriesKey: "lowestTemp",
      col: numericColumn(t, "detail.tbllowalarm", "low_temp"),
    },
    {
      seriesKey: "highestTemp",
      col: numericColumn(t, "detail.tblhighalarm", "high_temp"),
    },
    // Inside temp: plain normally, red+bold only when its OWN value breaches the
    // threshold (mirrors the PDF — in-range reads plain so breaches stand out).
    {
      seriesKey: "insideTemp",
      col: {
        ...numericColumn(t, "detail.tblinside", "room_temp"),
        render: (v: any, record: any) =>
          isRoomBreach(record) ? breachCell(v, record) : v,
      },
    },
    // Realtime inside temp = the ALARM reading: plain normally, red+bold the
    // moment its own value breaches the threshold.
    {
      seriesKey: "realTemp",
      col: {
        ...numericColumn(t, "detail.tblinsidert", "real_temp"),
        render: (v: any, record: any) =>
          isTempBreach(record) ? breachCell(v, record) : v,
      },
    },
    {
      seriesKey: "evapCoil",
      col: numericColumn(t, "detail.tblcoil", "evap_temp"),
    },
    {
      seriesKey: "probe2R",
      col: numericColumn(t, "detail.tblcoilrt", "probe2R"),
    },
  ];
  const columns = columnDefs
    .filter((d) => !d.seriesKey || !hiddenSeries.has(d.seriesKey))
    .map((d) => d.col);

  // Chart data: feed the recharts history chart from the aggregated rows. The
  // threshold/set-point lines (high/low alarm, set point) are plotted as dashed
  // STEP SERIES, not constant reference lines — those values change over time
  // (the operator edits them on the device), so the lines must follow the data.
  const chartRows = dataTemp?.results ?? [];
  const tempUnitSuffix = filterTemp.unit === "f" ? "°F" : "°C";
  const num = (v: any): number | null => {
    if (v === null || v === undefined || v === "-" || v === "") return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  // Apply the filter modal's data-resolution to the on-page chart + table.
  // Averages each window (dividing by the actual sample count) and keeps missing
  // minutes as gaps. Breach itemization (owner's spec): every raw out-of-range
  // reading is merged back as its OWN row so the table itemizes each violation
  // point; the window rows average only the in-range samples of the governed
  // columns (a breaching sample lives in its own row — no double counting, no
  // phantom averaged breach values).
  const displayRows: any[] = useMemo(() => {
    const rows = chartRows as any[];
    // The filter modal only offers a resolution ≥ the unit's floor (weeks ≥ 10m,
    // months ≥ 30m) and the unit-change effect snaps to it, so a wide range can
    // never arrive here at a row-exploding 1-min resolution.
    const res = filterTemp?.resolution ?? 1;
    return downsampleAvg(rows, res, TEMP_NUMERIC_KEYS, {
      breachItemize: {
        keys: ["insideTemp", "realTemp"],
        minKey: "lowestTemp",
        maxKey: "highestTemp",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRows, filterTemp?.resolution]);

  // PDF export (slice 6). The whole jsPDF path is dynamically imported so it
  // stays out of the main bundle. Primary sensor for stats/breach = inside temp.
  const exportPdf = async (opts: {
    intervalMin: number;
    includeChart: boolean;
    seriesKeys: string[];
  }) => {
    const readings = (chartRows as any[])
      .map((r) => ({
        ...r,
        value: num(r?.realTemp), // alarm reading = realtime inside probe
        min: num(r?.lowestTemp), // per-record threshold in effect at this time
        max: num(r?.highestTemp),
      }))
      .filter((r) => r.value !== null && r.value !== undefined);
    if (readings.length === 0) return;
    // Thai UI → Thai abbreviated month + CE year ("22 ก.ค. 2026 08:42")
    const fmtFull = (d: any) =>
      currentLanguage === "th"
        ? dayjs(d).locale("th").format("D MMM YYYY HH:mm")
        : dayjs(d).format("DD/MM/YYYY HH:mm");
    // Issue date: date only (no time), FULL month name; Thai shows the
    // Buddhist-era year ("22 กรกฎาคม 2569") — the document-formality convention
    const now = dayjs();
    const issueDateLabel =
      currentLanguage === "th"
        ? `${now.locale("th").format("D MMMM")} ${now.year() + 543}`
        : now.format("D MMMM YYYY");
    // Report granularity from the selected period unit
    // (hours→รายชั่วโมง, days→รายวัน, weeks→รายสัปดาห์, months→รายเดือน)
    const periodLabel = t(
      `detail.period${filterDeviceDetail.unit}` as any
    ) as string;
    const first = readings[0];
    const last = readings[readings.length - 1];
    const diffMin = Math.max(
      0,
      dayjs(last.time).diff(dayjs(first.time), "minute")
    );
    const durationLabel =
      diffMin >= 60
        ? `${Math.floor(diffMin / 60)} ${t("detail.hour")} ${
            diffMin % 60
          } ${t("detail.minunit")}`
        : `${diffMin} ${t("detail.minunit")}`;
    // Capture the on-screen recharts chart as a PNG for the report (dynamic
    // import keeps html2canvas out of the main bundle). During the snapshot the
    // chart re-renders in export mode (no zoom brush; the snapshot target also
    // excludes the legend chips) → a clean ref-certificate-style chart.
    let chartImage: string | undefined;
    if (opts.includeChart && opts.seriesKeys.length > 0) {
      setChartExportMode(true);
      setExportSeriesKeys(opts.seriesKeys);
      try {
        // wait for the export-mode re-render (animations are off)
        await new Promise((r) => setTimeout(r, 400));
        const chartEl = document.getElementById("temp-history-chart");
        if (chartEl) {
          const html2canvas = (await import("html2canvas")).default;
          const canvas = await html2canvas(chartEl, {
            scale: 2,
            backgroundColor: "#ffffff",
            logging: false,
          });
          chartImage = canvas.toDataURL("image/png");
        }
      } catch {
        chartImage = undefined;
      } finally {
        setChartExportMode(false);
        setExportSeriesKeys(null);
      }
    }
    // Device photo → data URI for the PDF thumbnail. react-pdf can't rely on a
    // presigned-S3 URL (needs CORS to fetch), so we draw the already-loaded
    // <img> onto a canvas and export a data URI. Any failure (CORS taint, no
    // image) → undefined and the thumbnail is simply omitted.
    let deviceImage: string | undefined;
    const rawImg = deviceDetail?.deviceInfo?.image ?? "";
    if (rawImg) {
      try {
        deviceImage = await new Promise<string | undefined>((resolve) => {
          const img = new window.Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            try {
              const side = Math.min(img.naturalWidth, img.naturalHeight);
              const c = document.createElement("canvas");
              c.width = 256;
              c.height = 256;
              const ctx = c.getContext("2d");
              if (!ctx) return resolve(undefined);
              // center-crop to 1:1
              ctx.drawImage(
                img,
                (img.naturalWidth - side) / 2,
                (img.naturalHeight - side) / 2,
                side,
                side,
                0,
                0,
                256,
                256
              );
              resolve(c.toDataURL("image/jpeg", 0.85));
            } catch {
              resolve(undefined);
            }
          };
          img.onerror = () => resolve(undefined);
          img.src = displayImage(rawImg);
        });
      } catch {
        deviceImage = undefined;
      }
    }
    // Reading-table columns follow the sensors selected in the export modal
    // (same picker as the chart). Fixed sensor order + short table headers; the
    // kind drives cell coloring in the PDF (realTemp = alarm, bounds = muted).
    // lower bound first, upper bound last, measured sensors bracketed between —
    // so a row reads "ด้านต่ำ → ค่าจริง → ด้านสูง" for at-a-glance comparison
    const SENSOR_ORDER = [
      "lowestTemp",
      "insideTemp",
      "realTemp",
      "evapCoil",
      "probe2R",
      "setPointTemp",
      "highestTemp",
    ];
    const sensorLabel: Record<string, string> = {
      insideTemp: t("detail.tblinside"),
      realTemp: t("detail.tblinsidert"),
      evapCoil: t("detail.tblcoil"),
      probe2R: t("detail.tblcoilrt"),
      highestTemp: t("detail.tblhighalarm"),
      lowestTemp: t("detail.tbllowalarm"),
      setPointTemp: t("detail.tblsettemp"),
    };
    // realTemp = the alarm sensor; insideTemp = the primary recorded sensor.
    // Both are governed by the row threshold [low, high], so each is colored
    // green/red + △ against its OWN value. Coil sensors have a different range,
    // so they stay plain; bounds/set-point are muted reference values.
    const sensorKind = (
      k: string
    ): "alarm" | "primary" | "bound" | "sensor" =>
      k === "realTemp"
        ? "alarm"
        : k === "insideTemp"
        ? "primary"
        : k === "highestTemp" || k === "lowestTemp" || k === "setPointTemp"
        ? "bound"
        : "sensor";
    const sensorColumns = SENSOR_ORDER.filter((k) =>
      opts.seriesKeys.includes(k)
    ).map((k) => ({ key: k, label: sensorLabel[k] ?? k, kind: sensorKind(k) }));

    const { generateTemperatureReport } = await import(
      "@utils/pdf/temperatureReport"
    );
    // Report number (header) + document id (footer) for traceability.
    // Format: TMP-<last 4 of MAC>-<YYMMDD>-<HHmm> (e.g. TMP-4444-260722-0944)
    // — short balanced segments; device first so filed reports group per
    // device then chronologically. Seconds dropped for readability — true
    // uniqueness is guaranteed by the docId uuid in the footer.
    const macAddress =
      deviceDetail?.controlInfo?.macAddress ??
      deviceDetail?.deviceInfo?.macAddress ??
      "-";
    const mac4 = macAddress
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(-4)
      .toUpperCase();
    const docRef = `TMP-${mac4 || "0000"}-${dayjs().format("YYMMDD-HHmm")}`;
    const docId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? (crypto as any).randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    const blob = await generateTemperatureReport({
      orgName: "CORNTROL",
      deviceImage,
      docRef,
      periodLabel,
      docId,
      recordedByName: userInfo?.user_fullname || "-",
      recordedByEmail: userInfo?.email || "",
      deviceName: deviceDetail?.deviceInfo?.name ?? "-",
      macAddress,
      deviceDescription: dataReportDetail?.description ?? "-",
      locationName: deviceDetail?.name ?? "-",
      fromLabel: fmtFull(first.time),
      toLabel: fmtFull(last.time),
      durationLabel,
      printedAtLabel: issueDateLabel,
      printedAtTime: now.format("HH:mm"),
      lang: currentLanguage,
      unitSuffix: tempUnitSuffix,
      intervalMin: opts.intervalMin,
      threshold: {
        // banner shows the current (latest) threshold; breach uses per-record
        min: last.min,
        max: last.max,
      },
      readings,
      sensorColumns: sensorColumns.length ? sensorColumns : undefined,
      chartImage,
      alertDelaySec,
      alarmLogs,
      labels: {
        title: t("detail.reporttitle"),
        device: t("detail.name"),
        deviceInfo: t("detail.deviceinfo"),
        description: t("detail.desc"),
        mac: t("detail.mac"),
        location: t("monitoring.location"),
        recordedBy: t("detail.issuedby"),
        reportPeriod: t("detail.reporttype"),
        page: t("detail.pagelabel"),
        dataPeriod: t("detail.dataperiod"),
        durationCol: t("detail.durationcol"),
        rowUnit: t("detail.rowunit"),
        fromRecorded: t("detail.fromrecorded"),
        firstReading: t("detail.firstreading"),
        lastReading: t("detail.lastreading"),
        sensorName: t("detail.realtemp"),
        summary: t("detail.reportsummary"),
        lowest: t("detail.lowest"),
        highest: t("detail.highest"),
        average: t("detail.average"),
        total: t("detail.totalreadings"),
        duration: t("detail.duration"),
        thresholdRange: t("detail.thresholdrange"),
        breachCount: (n: number) =>
          `${t("detail.outofrange")} ${n} ${t("detail.times")}`,
        briefCount: (n: number) =>
          `${t("detail.briefexcursion")} ${n} ${t("detail.times")}`,
        briefTag: t("detail.brieftag"),
        alertDelay: t("detail.alertdelay"),
        secUnit: t("detail.secunit"),
        printedAt: t("detail.printedat"),
        docRef: t("detail.docref"),
        chart: t("detail.reportchart"),
        breachPeriods: t("detail.breachperiods"),
        times: t("detail.times"),
        readingList: t("detail.readinglist"),
        time: t("detail.time"),
        deviceCol: t("detail.devicecol"),
        sensorCol: t("detail.sensorcol"),
        colStart: t("detail.colstart"),
        colEnd: t("detail.colend"),
        colType: t("detail.coltype"),
        colPeak: t("detail.colpeak"),
        colLimit: t("detail.collimit"),
        colCount: t("detail.colcount"),
        overHigh: t("detail.overhigh"),
        underLow: t("detail.underlow"),
        showEvery: t("detail.showevery"),
        minUnit: t("detail.minunit"),
        hourUnit: t("detail.hour"),
        inspector: t("detail.inspector"),
        authorizer: t("detail.authorizer"),
        recipient: t("detail.recipient"),
        signHere: t("detail.signhere"),
        signDate: t("detail.signdate"),
      },
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Filename = brand + document number, so a printed report traces straight
    // back to its file (ref-certificate convention)
    a.download = `CORNTROL-${docRef}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tempSeries: ChartSeries[] = [
    // insideTemp is the star line — deep blue, gradient fill, topmost, heaviest
    {
      key: "insideTemp",
      name: t("detail.roomtemp"),
      color: "#3b82f6",
      primary: true,
    },
    { key: "realTemp", name: t("detail.realtemp"), color: "#38bdf8" },
    { key: "evapCoil", name: t("detail.evaptemp"), color: "#ec4899" },
    { key: "probe2R", name: t("detail.realtemp2"), color: "#f472b6" },
    // short "ขอบเขตด้านสูง/ต่ำ" labels (same keys as the table headers) — the
    // full alarm wording was too long for the legend chips / export modal
    {
      key: "highestTemp",
      name: t("detail.tblhighalarm"),
      color: "#ef4444",
      dashed: true,
    },
    {
      key: "lowestTemp",
      name: t("detail.tbllowalarm"),
      color: "#f59e0b",
      dashed: true,
    },
    {
      key: "setPointTemp",
      name: t("detail.settemp"),
      color: "#9ca3af",
      dashed: true,
    },
  ];

  // Effective visible series: the export selection while snapshotting,
  // otherwise the on-page chip toggles.
  const visibleSeriesKeys =
    exportSeriesKeys ??
    tempSeries.filter((s) => !hiddenSeries.has(s.key)).map((s) => s.key);

  // "สูง 39° / ต่ำ 20°" right-edge markers (b-coldtrack style) at the LATEST
  // threshold values — label-only, the dashed step series draws the line itself
  const lastNum = (key: string): number | null => {
    for (let i = displayRows.length - 1; i >= 0; i--) {
      const v = num(displayRows[i]?.[key]);
      if (v !== null) return v;
    }
    return null;
  };
  const lastHigh = visibleSeriesKeys.includes("highestTemp")
    ? lastNum("highestTemp")
    : null;
  const lastLow = visibleSeriesKeys.includes("lowestTemp")
    ? lastNum("lowestTemp")
    : null;
  const chartRefLines = [
    ...(lastHigh !== null
      ? [
          {
            value: lastHigh,
            label: `${t("detail.hishort")} ${lastHigh}°`,
            color: "#ef4444",
            labelOnly: true,
          },
        ]
      : []),
    ...(lastLow !== null
      ? [
          {
            value: lastLow,
            label: `${t("detail.loshort")} ${lastLow}°`,
            color: "#f59e0b",
            labelOnly: true,
          },
        ]
      : []),
  ];

  // At-a-glance summary (stat tiles + threshold/breach banner) — same math the
  // PDF uses, computed from the FULL rows (not the downsampled displayRows) so
  // min/max/avg/count and the breach count are accurate. Primary sensor = inside
  // temp; threshold is per-record, so the banner shows the latest one.
  const summary = useMemo(() => {
    const rows = chartRows as any[];
    // Breach/threshold evaluate against realTemp — the reading the device
    // alarm uses.
    const readings = rows
      .map((r) => ({
        time: r?.time,
        value: num(r?.realTemp),
        min: num(r?.lowestTemp),
        max: num(r?.highestTemp),
      }))
      .filter((r) => r.value !== null && r.value !== undefined) as {
      time: string;
      value: number;
      min: number | null;
      max: number | null;
    }[];
    if (readings.length === 0) return null;
    // Stat tiles (min/max/avg/count) use the RECORDED inside temp, matching
    // the PDF's summary tiles.
    const insideReadings = rows
      .map((r) => ({ time: r?.time, value: num(r?.insideTemp) }))
      .filter((r) => r.value !== null) as { time: string; value: number }[];
    const stats = computeStats(
      insideReadings.length > 0 ? insideReadings : readings
    );
    const breaches = detectBreachesDynamic(readings);
    // Real alarms (red) vs excursions the device never alarmed on (soft amber),
    // modeled as alarm-ACTIVE WINDOWS. Ground truth = the device activity log
    // (tHi/tLo fired/cleared markers — the delay in effect at that moment is
    // already applied); when the log fetch failed, reconstruct windows from
    // event start + timeAlertDelay.
    const alarmWindows = alarmLogs
      ? buildAlarmWindows(alarmLogs)
      : heuristicAlarmWindows(breaches, alertDelaySec);
    const severities: BreachSeverity[] = breaches.map((b) =>
      classifyBreachByWindows(b, alarmWindows)
    );
    const alarmCount = severities.filter((s) => s === "alarm").length;
    const last = readings[readings.length - 1];
    const diffMin = Math.max(
      0,
      dayjs(last.time).diff(dayjs(readings[0].time), "minute")
    );
    return {
      stats,
      breaches, // full out-of-range events (for the Excel breaches sheet)
      severities, // per-event severity, index-aligned with breaches
      alarmWindows, // alarm-ACTIVE periods (log-based or reconstructed)
      breachCount: breaches.length,
      alarmCount,
      briefCount: breaches.length - alarmCount,
      diffMin,
      from: readings[0].time,
      to: last.time,
      // first/latest realTemp values for the PDF-mirroring side boxes
      firstVal: readings[0].value,
      lastVal: last.value,
      thMin: last.min,
      thMax: last.max,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRows, alertDelaySec, alarmLogs]);

  const fmtVal = (v: number | null | undefined): string =>
    v === null || v === undefined || Number.isNaN(v)
      ? "-"
      : `${Math.round(v * 10) / 10}${tempUnitSuffix}`;
  const durationLabel = summary
    ? summary.diffMin >= 60
      ? `${Math.floor(summary.diffMin / 60)} ${t("detail.hour")} ${
          summary.diffMin % 60
        } ${t("detail.minunit")}`
      : `${summary.diffMin} ${t("detail.minunit")}`
    : "";
  const summaryOverHigh =
    !!summary &&
    summary.thMax !== null &&
    summary.stats.max !== null &&
    summary.stats.max > summary.thMax;
  // Report granularity label, same mapping the PDF header uses
  // (hours→รายชั่วโมง, days→รายวัน, weeks→รายสัปดาห์, months→รายเดือน)
  const summaryPeriodLabel = t(
    `detail.period${filterDeviceDetail.unit}` as any
  ) as string;
  const fmtBadgeTime = (time: string) => dayjs(time).format("DD/MM HH:mm");

  // Thousand-separated counts + a caption explaining the current resolution and
  // how many rows are shown out of what was recorded.
  const fmtNum = (n: number) => n.toLocaleString("en-US");
  const shownCount = displayRows.filter(
    (r: any) => num(r?.insideTemp) !== null
  ).length;
  // The EFFECTIVE interval of what's shown — the source for weeks/months is
  // already pre-aggregated (~5-/10-min), so a finer resolution can't add points.
  // Report the real median gap between shown rows, not the requested resolution,
  // so the caption never claims "every 1 min" when the data is coarser.
  const shownIntervalMin = (() => {
    const times = (displayRows as any[])
      .map((r) => new Date(r?.time).getTime())
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
    if (times.length < 2) return filterTemp?.resolution ?? 1;
    const gaps: number[] = [];
    for (let i = 1; i < times.length; i++) {
      gaps.push((times[i] - times[i - 1]) / 60000);
    }
    gaps.sort((a, b) => a - b);
    return Math.max(1, Math.round(gaps[Math.floor(gaps.length / 2)]));
  })();
  const rowsCaption = summary
    ? (() => {
        const head = `${t("detail.showevery")} ${shownIntervalMin} ${t(
          "detail.minunit"
        )}`;
        // Read as "showing 1,008 of 2,016 rows" — the 2,016 equals the total
        // tile, so the two numbers relate (subset) instead of looking duplicated.
        // Only shown when the resolution actually reduced the rows.
        return shownCount < summary.stats.count
          ? `${head} · ${fmtNum(shownCount)} ${t("detail.fromrecorded")} ${fmtNum(
              summary.stats.count
            )} ${t("detail.rowunit")}`
          : `${head} · ${fmtNum(shownCount)} ${t("detail.rowunit")}`;
      })()
    : "";

  // Excel export = 3 sheets: Summary (device + stats), History (the shown rows),
  // Out-of-range periods (breach events). Breach math uses realTemp, like the
  // rest of the page.
  const fmtDur = (start: string, end: string): string => {
    const m = Math.max(0, dayjs(end).diff(dayjs(start), "minute"));
    if (m < 60) return `${m} ${t("detail.minunit")}`;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return mm > 0
      ? `${h} ${t("detail.hour")} ${mm} ${t("detail.minunit")}`
      : `${h} ${t("detail.hour")}`;
  };
  const breachTypeLabel = (ty: string) =>
    ty === "LOW"
      ? t("detail.underlow")
      : ty === "HIGH"
      ? t("detail.overhigh")
      : `${t("detail.underlow")}/${t("detail.overhigh")}`;
  const breachPeak = (b: any) =>
    b?.type === "LOW"
      ? fmtVal(b?.min)
      : b?.type === "HIGH"
      ? fmtVal(b?.max)
      : `${fmtVal(b?.min)}–${fmtVal(b?.max)}`;

  const excelHistoryHeader = [
    t("detail.time"),
    t("detail.settemp"),
    t("detail.lowtempalarm"),
    t("detail.hightempalarm"),
    t("detail.roomtemp"),
    t("detail.realtemp"),
    t("detail.evaptemp"),
    t("detail.realtemp2"),
    t("detail.xlsstatus"),
  ];
  // Status column carries what the on-screen colors say — Excel has no cell
  // colors, so without it a breaching row is indistinguishable from a normal
  // one in the file. Same severity rules as the table (alarm-window based).
  const excelRowStatus = (e: any): string => {
    const v = num(e?.realTemp);
    const vr = num(e?.insideTemp);
    const lo = num(e?.lowestTemp);
    const hi = num(e?.highestTemp);
    const breach = (x: number | null) =>
      x !== null &&
      ((lo !== null && x < lo) || (hi !== null && x > hi));
    if (!breach(v) && !breach(vr)) return "-";
    return severityForRow({
      _time: e?.time ?? "",
      _real: v,
      _low: lo,
      _high: hi,
    }) === "brief"
      ? t("detail.briefexcursion")
      : t("detail.outofrange");
  };
  const excelHistoryData = displayRows.map((e: any) => ({
    Time: dayjs(e?.time ?? "").format("DD/MM/YYYY HH:mm"),
    "Set Temp": toFixedOrDash(e?.setPointTemp),
    "Low Temp Alarm": toFixedOrDash(e?.lowestTemp),
    "High Temp Alarm": toFixedOrDash(e?.highestTemp),
    "Room Temp": toFixedOrDash(e?.insideTemp),
    "Real Temp (P1)": toFixedOrDash(e?.realTemp),
    "Evap Temp": toFixedOrDash(e?.evapCoil),
    "Real Temp (P2)": toFixedOrDash(e?.probe2R),
    Status: excelRowStatus(e),
  }));

  const F = t("detail.xlsfield");
  const V = t("detail.xlsvalue");
  const kv = (label: string, value: any) => ({ [F]: label, [V]: value });
  const excelInfoData = summary
    ? [
        kv(t("detail.name"), deviceDetail?.deviceInfo?.name ?? "-"),
        kv(
          t("detail.mac"),
          deviceDetail?.controlInfo?.macAddress ??
            deviceDetail?.deviceInfo?.macAddress ??
            "-"
        ),
        kv(t("monitoring.location"), deviceDetail?.name ?? "-"),
        kv(t("detail.reporttype"), summaryPeriodLabel),
        kv(
          t("detail.dataperiod"),
          `${dayjs(summary.from).format("DD/MM/YYYY HH:mm")} - ${dayjs(
            summary.to
          ).format("DD/MM/YYYY HH:mm")} (${durationLabel})`
        ),
        kv(
          t("detail.thresholdrange"),
          `${fmtVal(summary.thMin)} — ${fmtVal(summary.thMax)}`
        ),
        kv(t("detail.lowest"), fmtVal(summary.stats.min)),
        kv(t("detail.highest"), fmtVal(summary.stats.max)),
        kv(t("detail.average"), fmtVal(summary.stats.avg)),
        kv(t("detail.datacount"), fmtNum(summary.stats.count)),
        // same caption as the on-page summary — explains why the history tab
        // holds fewer rows than the recorded count when a coarser data
        // resolution is selected ("แสดงทุก 5 นาที · 13 จาก 60 รายการ")
        kv(t("detail.xlshistory"), rowsCaption),
        kv(
          t("detail.outofrange"),
          `${fmtNum(summary.alarmCount)} ${t("detail.times")}` +
            (summary.briefCount > 0
              ? ` (${t("detail.briefexcursion")} ${fmtNum(
                  summary.briefCount
                )} ${t("detail.times")})`
              : "")
        ),
        kv(t("detail.printedat"), dayjs().format("DD/MM/YYYY HH:mm")),
      ]
    : [];

  const excelBreachData = (summary?.breaches ?? []).map(
    (b: any, i: number) => ({
      "#": i + 1,
      [t("detail.colstart")]: dayjs(b?.start).format("DD/MM/YYYY HH:mm"),
      [t("detail.colend")]: dayjs(b?.end).format("DD/MM/YYYY HH:mm"),
      [t("detail.durationcol")]: fmtDur(b?.start, b?.end),
      [t("detail.coltype")]: breachTypeLabel(b?.type),
      [t("detail.colpeak")]: breachPeak(b),
      [t("detail.colcount")]: b?.count,
      // alarm vs never-alarmed excursion, matching the web/PDF split — same
      // "สถานะ" header as the history sheet's severity column
      [t("detail.xlsstatus")]:
        summary?.severities?.[i] === "brief"
          ? t("detail.briefexcursion")
          : t("detail.outofrange"),
    })
  );

  const excelSheets = [
    { name: t("detail.xlsinfo"), data: excelInfoData },
    {
      name: t("detail.xlshistory"),
      header: excelHistoryHeader,
      data: excelHistoryData,
    },
    {
      name: t("detail.xlsbreaches"),
      // a clean period exports an explicit "no out-of-range periods" row —
      // an empty sheet (no headers at all) reads as a broken file
      data: excelBreachData.length
        ? excelBreachData
        : [{ [F]: t("detail.xlsnobreach") }],
    },
  ];

  // Excel + PDF download buttons — relocated from the headline card into the
  // summary action row (top-right of the summary block).
  const downloadButtons = (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      {/* Excel is WYSIWYG (exports displayRows = the table as shown, incl. the
          filter's data resolution) — the tooltip makes that discoverable
          instead of adding a second resolution picker that could let the file
          disagree with the screen */}
      <Tooltip
        title={t("detail.excelwysiwyg")}
        color="#ffffff"
        overlayInnerStyle={cardTooltipStyle}
      >
        <span>
          <Excel
            fileName={`${id}-${t("detail.temp")}-${dayjs().format(
              "YYYYMMDDHHmmss"
            )}`}
            sheets={excelSheets as any}
            button={
              <div
                className="size-14 weight-600 color-black pointer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  border: "1px solid #e2e8f0",
                  padding: "6px 14px",
                  borderRadius: 8,
                  whiteSpace: "nowrap",
                }}
              >
                <DownloadIcon />
                {t("detail.downloadexcel")}
              </div>
            }
          />
        </span>
      </Tooltip>
      <div
        className="size-14 weight-600 color-black pointer"
        onClick={() => setModal({ open: "export" })}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          border: "1px solid #e2e8f0",
          padding: "6px 14px",
          borderRadius: 8,
          whiteSpace: "nowrap",
        }}
      >
        <DownloadIcon />
        {t("detail.exportpdf")}
      </div>
    </div>
  );

  return (
    <MetricDetailLayout
      title={t("detail.tempdetail")}
      metricLabel={t("detail.temp")}
      devicesLabel={t("devices.head")}
      onBack={() => navigate(`/device-detail/${id}`)}
      onBreadcrumbDevices={() => navigate(`/devices`)}
      deviceName={deviceDetail?.deviceInfo?.name ?? ""}
      image={
        (deviceDetail?.deviceInfo?.image ?? "")
          ? displayImage(deviceDetail?.deviceInfo?.image ?? "")
          : imgList.ex
      }
      locationName={deviceDetail?.name ?? ""}
      soundSlot={renderSound(
        deviceDetail?.controlInfo?.macAddress ?? "",
        deviceDetail?.controlInfo?.soundAlarm ?? false,
        deviceDetail?.controlInfo?.control?.alarm ?? false
      )}
      headerActions={downloadButtons}
      headlineExtra={
        summary ? (
          // Mirrors the PDF summary section 1:1 so this block reads as a live
          // preview of the certificate: first/latest side boxes flanking the 4
          // stat tiles, then the report-type + recording-period line, then the
          // threshold/breach banner. Same math, same colors (min plain, max red
          // only when over — matching the PDF tiles).
          <SummaryBlock>
            <div className="sum__head">{t("detail.reportsummary")}</div>
            <div className="sum__row">
              <div className="sum__side sum__side--first">
                <div className="sum__side-badge">
                  {t("detail.firstreading")} {fmtBadgeTime(summary.from)}
                </div>
                <div className="sum__side-val">{fmtVal(summary.firstVal)}</div>
                <div className="sum__side-cap">{t("detail.realtemp")}</div>
              </div>
              <div className="sum__center">
                <div className="sum__tiles">
                  <div className="sum__tile">
                    <div className="sum__tile-label">{t("detail.lowest")}</div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {fmtVal(summary.stats.min)}
                    </div>
                  </div>
                  <div className="sum__tile">
                    <div className="sum__tile-label">{t("detail.highest")}</div>
                    <div
                      className="sum__tile-value"
                      style={{ color: summaryOverHigh ? "#dc2626" : "#1f2328" }}
                    >
                      {fmtVal(summary.stats.max)}
                    </div>
                  </div>
                  <div className="sum__tile">
                    <div className="sum__tile-label">{t("detail.average")}</div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#1f2328" }}
                    >
                      {fmtVal(summary.stats.avg)}
                    </div>
                  </div>
                  <div className="sum__tile">
                    <div className="sum__tile-label">
                      {t("detail.totalreadings")}
                    </div>
                    <div
                      className="sum__tile-value"
                      style={{ color: "#334155" }}
                    >
                      {fmtNum(summary.stats.count)}
                    </div>
                  </div>
                </div>
                {/* report type + recording period under the tiles, separated by
                    a thin rule spanning only the center column — as in the PDF */}
                <div className="sum__duration">
                  {t("detail.reporttype")}: {summaryPeriodLabel}
                  {"  ·  "}
                  {t("detail.dataperiod")}: {durationLabel}
                </div>
              </div>
              <div className="sum__side sum__side--last">
                <div className="sum__side-badge">
                  {t("detail.lastreading")} {fmtBadgeTime(summary.to)}
                </div>
                <div className="sum__side-val">{fmtVal(summary.lastVal)}</div>
                <div className="sum__side-cap">{t("detail.realtemp")}</div>
              </div>
            </div>
            <div className="sum__rows">{rowsCaption}</div>
            <div
              className={`sum__banner ${
                summary.alarmCount > 0
                  ? "sum__banner--breach"
                  : summary.briefCount > 0
                  ? "sum__banner--brief"
                  : "sum__banner--ok"
              }`}
            >
              <div>
                <div className="sum__banner-title">
                  {t("detail.thresholdrange")}
                </div>
                <div className="sum__banner-range">
                  {t("detail.lowest")} {fmtVal(summary.thMin)} —{" "}
                  {t("detail.highest")} {fmtVal(summary.thMax)}
                </div>
              </div>
              {/* real alarms (>= device alert delay) in red; excursions that
                  returned in-range within the delay as a separate soft chip */}
              <div className="sum__banner-chips">
                {summary.alarmCount > 0 || summary.briefCount === 0 ? (
                  <div className="sum__banner-count">
                    {summary.alarmCount > 0 ? "⚠ " : ""}
                    {t("detail.outofrange")} {summary.alarmCount}{" "}
                    {t("detail.times")}
                  </div>
                ) : null}
                {summary.briefCount > 0 ? (
                  <div className="sum__banner-count sum__banner-count--brief">
                    {t("detail.briefexcursion")} {summary.briefCount}{" "}
                    {t("detail.times")}
                  </div>
                ) : null}
              </div>
            </div>
          </SummaryBlock>
        ) : null
      }
      breadcrumbActions={
        <>
          <MetricFilterControls
            filterDeviceDetail={filterDeviceDetail}
            plan={plan}
            listUnit={listUnit}
            listPeriod={listPeriod}
          />
          <div
            className="detail__filter-btn"
            onClick={() => setModal({ open: "filter_temp" })}
          >
            <AdjustmentsIcon />
          </div>
        </>
      }
      filterBar={
        <div className="detail__filter">
          <div
            className="detail__head-2"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <ChartLineIcon />
            <span className="color-black size-20 font-sukhumvitSet-bold">
              {t("detail.reportchart")}
            </span>
          </div>
        </div>
      }
      chart={
        chartRows.length > 0 ? (
          <div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 8,
                margin: "18px 0 6px",
              }}
            >
              {tempSeries.map((s) => {
                const hidden = hiddenSeries.has(s.key);
                return (
                  <div
                    key={s.key}
                    className="pointer"
                    onClick={() => toggleSeries(s.key)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "4px 10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: 16,
                      opacity: hidden ? 0.4 : 1,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        background: s.color,
                      }}
                    />
                    <span className="size-12 font-sukhumvitSet-text color-black">
                      {s.name}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* id here (chart only, no legend chips) — the PDF snapshot is a
                clean ref-certificate-style chart */}
            <div className="detail__table" id="temp-history-chart">
              {/* key remounts the chart when the row count changes, so
                  recharts re-measures on a settled layout — guards against the
                  blank first paint when data lands mid-transition */}
              <ChartTemperatureHistory
                key={displayRows.length}
                data={displayRows}
                timeKey="time"
                unitSuffix={tempUnitSuffix}
                series={tempSeries.filter((s) =>
                  visibleSeriesKeys.includes(s.key)
                )}
                refLines={chartRefLines}
                hideBrush={chartExportMode}
                // Taller on screen so the trace is readable; the PDF snapshot
                // keeps the original height so the exported report's chart
                // proportions (and its row budget) are unchanged.
                height={chartExportMode ? 160 : 260}
              />
            </div>
          </div>
        ) : (
          // Never leave a bare white gap under the "กราฟอุณหภูมิ" header: show
          // a loading state until the report lands, and an explicit empty state
          // when the range genuinely has no rows.
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 220,
              border: "1px dashed #e2e8f0",
              borderRadius: 12,
              margin: "18px 0 0",
              color: "#94a3b8",
              fontSize: 14,
            }}
          >
            {Array.isArray(dataTemp?.results)
              ? t("detail.nodatarange")
              : t("detail.loadingdata")}
          </div>
        )
      }
      table={
        <div className="detail__table">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              margin: "8px 0 16px",
            }}
          >
            <TableIcon />
            <span className="color-black size-20 font-sukhumvitSet-bold">
              {t("detail.temptable")}
            </span>
          </div>
          <Table
            pagination={{
              locale: { items_per_page: t("detail.perpage") },
              showLessItems: true,
              showTotal: (total: number, range: [number, number]) =>
                `${t("detail.showing")} ${fmtNum(range[0])}-${fmtNum(
                  range[1]
                )} ${t("detail.oftotal")} ${fmtNum(total)} ${t(
                  "detail.rowunit"
                )}`,
            }}
            showSorterTooltip={false}
            columns={columns}
            rowClassName={(record: any) =>
              isTempBreach(record)
                ? severityForRow(record) === "brief"
                  ? "row-breach-brief"
                  : "row-breach"
                : ""
            }
            dataSource={displayRows.map(
              (e: any, index: number) => {
                return {
                  key: index,
                  time: dayjs(e?.time ?? "").format("DD/MM/YYYY HH:mm"),
                  set_temp: toFixedOrDash(e?.setPointTemp),
                  low_temp: toFixedOrDash(e?.lowestTemp),
                  high_temp: toFixedOrDash(e?.highestTemp),
                  room_temp: toFixedOrDash(e?.insideTemp),
                  real_temp: toFixedOrDash(e?.realTemp),
                  evap_temp: toFixedOrDash(e?.evapCoil),
                  probe2R: toFixedOrDash(e?.probe2R),
                  _real: num(e?.realTemp),
                  _room: num(e?.insideTemp),
                  _low: num(e?.lowestTemp),
                  _high: num(e?.highestTemp),
                  _time: e?.time ?? "", // raw time for breach-event lookup
                };
              }
            )}
          />
        </div>
      }
      modals={
        <>
      <ModalFilterTemp
        open={modal.open === "filter_temp"}
        hideType={true}
        onOk={() => {
          setModal({
            ...modal,
            open: "",
          });
        }}
        onClose={() =>
          setModal({
            ...modal,
            open: "",
          })
        }
      />
      <ModalExportReport
        open={modal.open === "export"}
        seriesOptions={tempSeries.map((s) => ({
          key: s.key,
          label: s.name,
          color: s.color,
        }))}
        defaultSeriesKeys={[
          "insideTemp",
          "realTemp",
          "highestTemp",
          "lowestTemp",
        ]}
        onClose={() => setModal({ ...modal, open: "" })}
        onConfirm={(opts) => {
          setModal({ ...modal, open: "" });
          exportPdf(opts);
        }}
      />
        </>
      }
    />
  );
}

